import { forwardRef } from "react";
import type { HTMLAttributes } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Lock } from "lucide-react";
import {
  Card,
  CardHeader,
  CardContent,
  CardFooter,
} from "@/components/shared/ui/Card";
import Badge from "@/components/shared/ui/Badge";
import Button from "@/components/shared/ui/Button";

interface ProjectCardProps extends Omit<HTMLAttributes<HTMLDivElement>, "id"> {
  project: {
    id: string;
    name: string;
    tagline?: string;
    thumbnail?: string;
    stage?: string;
    status?: string;
    user_type?: string;
    access_level?: "visible" | "locked" | "hidden";
    resource_count?: number;
    access_period?: string;

    // Investor-specific
    funding_target?: string;
    current_raise?: string;

    // Client-specific
    project_status?: string;
    completion_percentage?: number;

    // Visibility flags from user_project_access
    show_funding?: boolean;
    show_metrics?: boolean;
    show_status?: boolean;
    show_action_buttons?: boolean;
  };
  userType: "investor" | "client" | "admin";
  currentUserType?:
    | "investor"
    | "advisor"
    | "partner"
    | "board_member"
    | "client";
}

const ProjectCard = forwardRef<HTMLDivElement, ProjectCardProps>(
  ({ project, userType, currentUserType, className = "", ...props }, ref) => {
    const navigate = useNavigate();
    const isLocked = project.access_level === "locked";

    const handleClick = () => {
      if (!isLocked) {
        navigate(`/${userType}/project/${project.id}`);
      }
    };

    const getStageBadgeVariant = (stage?: string) => {
      switch (stage?.toLowerCase()) {
        case "pre-seed":
          return "warning";
        case "seed":
          return "info";
        case "series a":
        case "series b":
          return "success";
        default:
          return "neutral";
      }
    };

    const getStatusBadgeVariant = (status?: string) => {
      switch (status?.toLowerCase()) {
        case "active":
        case "in progress":
          return "success";
        case "complete":
          return "info";
        case "on hold":
          return "warning";
        default:
          return "neutral";
      }
    };

    const getUserTypeBadgeVariant = (type?: string) => {
      switch (type?.toLowerCase()) {
        case "investor":
          return "info";
        case "advisor":
          return "success";
        case "partner":
          return "warning";
        case "board_member":
          return "neutral";
        case "client":
          return "info";
        default:
          return "neutral";
      }
    };

    return (
      <Card
        ref={ref}
        variant="bordered"
        hover={!isLocked}
        className={`relative flex-shrink-0 w-[450px] flex flex-col ${className}`}
        {...props}
      >
        {/* Locked Overlay - Positioned at bottom */}
        {isLocked && (
          <div className="absolute inset-0 bg-gradient-to-t from-white via-white/40 to-transparent flex items-end justify-center pb-6 z-10">
            <div className="text-center bg-white border-4 border-black px-6 py-3 shadow-[8px_8px_0px_0px_rgba(0,0,0,1)]">
              <div className="flex items-center gap-2">
                <Lock className="w-5 h-5 text-black" />
                <p className="text-black font-bold text-sm uppercase">
                  Locked - Request Access
                </p>
              </div>
            </div>
          </div>
        )}

        <CardHeader>
          <div className="space-y-2">
            {/* Top Row: Thumbnail + Title + Badges */}
            <div className="flex items-start gap-3">
              {/* Thumbnail/Emoji */}
              <div className="text-3xl flex-shrink-0">
                {project.thumbnail || "📁"}
              </div>

              {/* Title - Takes available space */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-black text-black uppercase tracking-wide line-clamp-3">
                  {project.name}
                </h3>
              </div>

              {/* Badges - Aligned to top right */}
              {((userType === "investor" &&
                project.stage &&
                (project.show_status ?? true)) ||
                (userType === "client" && project.project_status) ||
                currentUserType) && (
                <div className="flex flex-col gap-2 flex-shrink-0">
                  {/* Stage/Status Badge - Only show if show_status is true (default true) */}
                  {userType === "investor" &&
                    project.stage &&
                    (project.show_status ?? true) && (
                      <Badge
                        variant={getStageBadgeVariant(project.stage)}
                        size="sm"
                      >
                        {project.stage}
                      </Badge>
                    )}
                  {userType === "client" && project.project_status && (
                    <Badge
                      variant={getStatusBadgeVariant(project.project_status)}
                      size="sm"
                    >
                      {project.project_status}
                    </Badge>
                  )}

                  {/* User Type Badge */}
                  {currentUserType && (
                    <Badge
                      variant={getUserTypeBadgeVariant(currentUserType)}
                      size="sm"
                    >
                      {currentUserType
                        .replace("_", " ")
                        .split(" ")
                        .map(
                          (word) => word.charAt(0).toUpperCase() + word.slice(1)
                        )
                        .join(" ")}{" "}
                      Permissions
                    </Badge>
                  )}
                </div>
              )}
            </div>

            {/* Bottom Row: Tagline spans full width */}
            {project.tagline && (
              <div className="pl-[52px]">
                <p className="text-xs text-gray-600 line-clamp-2">
                  {project.tagline}
                </p>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex-1">
          {/* Investor Metrics - Only show if show_funding is true (default true) */}
          {userType === "investor" && (project.show_funding ?? true) && (
            <div className="space-y-3">
              {project.funding_target && (
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 uppercase tracking-wide">
                      {project.current_raise ? "Target" : "Funding Target"}
                    </span>
                    <span className="font-bold text-black">
                      {project.current_raise
                        ? `${project.current_raise} / ${project.funding_target}`
                        : project.funding_target}
                    </span>
                  </div>
                  {/* Show progress bar if current_raise exists */}
                  {project.current_raise && (
                    <div className="w-full bg-gray-200 h-2 border border-black">
                      <div
                        className="bg-black h-full transition-all"
                        style={{
                          width: `${Math.min(
                            100,
                            (parseFloat(
                              project.current_raise.replace(/[^0-9.]/g, "")
                            ) /
                              parseFloat(
                                project.funding_target.replace(/[^0-9.]/g, "")
                              )) *
                              100
                          )}%`,
                        }}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Client Metrics */}
          {userType === "client" &&
            project.completion_percentage !== undefined && (
              <div className="space-y-3">
                <div>
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-gray-600 uppercase tracking-wide">
                      Progress
                    </span>
                    <span className="font-bold text-black">
                      {project.completion_percentage}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 h-2 border border-black">
                    <div
                      className="bg-black h-full transition-all"
                      style={{ width: `${project.completion_percentage}%` }}
                    />
                  </div>
                </div>
              </div>
            )}
        </CardContent>

        <CardFooter>
          {/* Resource Count */}
          <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
            <FileText className="w-4 h-4" />
            <span className="font-bold text-black">
              {project.resource_count || 0} resources
            </span>
          </div>

          {/* Access Period */}
          {project.access_period && (
            <div className="text-xs text-gray-500 italic mb-3">
              {project.access_period}
            </div>
          )}

          {/* View Project Button - Only show if show_action_buttons is true (default true) */}
          {(project.show_action_buttons ?? true) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleClick}
              disabled={isLocked}
              className="w-full"
            >
              {isLocked ? "Locked" : "View Project"}
            </Button>
          )}
        </CardFooter>
      </Card>
    );
  }
);

ProjectCard.displayName = "ProjectCard";

export default ProjectCard;
